import moment from "moment";
import { EMPTY_AR_FORM_PRODUCT } from "./arFormUtils";
import { parseFundAmount } from "./approvalFundListFundFields";

const parseTrailerRebate = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const normalized = String(value).replace(/%/g, "").trim();
  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? null : parsed;
};

export const mapApprovalFundToArProduct = (fund) => {
  if (!fund) return EMPTY_AR_FORM_PRODUCT();
  const riskRating = fund.riskRating ? Number(fund.riskRating) : null;

  return {
    fundName: fund.fundName ?? "",
    riskRating: Number.isNaN(riskRating) ? null : riskRating,
    trailerFee: parseTrailerRebate(fund.trailerRebate),
    complexProduct: fund.complexProduct === true,
    affiliatedFwd: false,
    fundCurrency: fund.fundCurrency ?? null,
    minSubscriptionAmount: parseFundAmount(fund.minSubscriptionAmount),
    subscribedAmount: null,
    contractNo: "",
    productSubscribed: false,
  };
};

export const buildApprovedFundOptions = (funds = []) =>
  funds.map((fund, index) => {
    const sourceIndex = fund.__sourceIndex ?? index;
    const parts = [fund.fundName, fund.isinCode, fund.ifastCode].filter(Boolean);
    return {
      value: String(sourceIndex),
      label: parts.join(" · "),
    };
  });

export const findApprovedFundOptionKey = (values = {}, funds = []) => {
  if (values._approvedFundKey != null && values._approvedFundKey !== "") {
    return String(values._approvedFundKey);
  }

  const fundName = String(values.fundName ?? "").trim().toLowerCase();
  if (!fundName) return null;

  const index = funds.findIndex(
    (fund) => String(fund.fundName ?? "").trim().toLowerCase() === fundName
  );
  return index >= 0 ? String(index) : null;
};

export const formatApprovedFundListLabel = (list) => {
  if (!list) return "";
  const name = list.name || list.code || "Approved fund list";
  const from = list.effectiveFrom ? new Date(list.effectiveFrom).toISOString().slice(0, 10) : "";
  const to = list.effectiveTo ? new Date(list.effectiveTo).toISOString().slice(0, 10) : "";
  if (from && to) return `${name} (${from} – ${to})`;
  if (from) return `${name} (from ${from})`;
  return name;
};

const hasOpenEffectiveTo = (effectiveTo) =>
  effectiveTo == null || effectiveTo === "";

export const isApprovalFundListEffective = (record = {}, asOf = new Date()) => {
  const effectiveFrom = record.effectiveFrom ?? record.date ?? null;
  if (!effectiveFrom) return false;

  const asOfDay = moment(asOf).startOf("day");
  const fromDay = moment(effectiveFrom).startOf("day");
  if (fromDay.isAfter(asOfDay, "day")) return false;

  const effectiveTo =
    record.effectiveTo === "" ? null : record.effectiveTo ?? null;
  if (hasOpenEffectiveTo(effectiveTo)) return true;

  return !moment(effectiveTo).startOf("day").isBefore(asOfDay, "day");
};

export const loadEffectiveApprovedFunds = async (api) => {
  const direct = await api.ApprovalFundList.getCurrentEffective();
  if (direct?.funds?.length || direct?.approvalFundList) {
    return {
      approvalFundList: direct.approvalFundList ?? null,
      funds: direct.funds ?? [],
    };
  }

  const page = await api.search({
    apiEntity: "approvalFundList",
    pageSize: 50,
    currentPage: 1,
    sort: { by: "effectiveFrom", order: "desc" },
    searchQuery: {},
    byAggregation: false,
  });

  const docs = page?.docs ?? [];
  const match = docs.find((doc) => isApprovalFundListEffective(doc));
  if (!match?._id) {
    return { approvalFundList: null, funds: [] };
  }

  const full = await api.getById({
    apiEntity: "approvalFundList",
    id: match._id,
  });

  return {
    approvalFundList: full ?? match,
    funds: full?.funds ?? match?.funds ?? [],
  };
};
